<template>
	<div class="runHistory">
		<div class="historyToolbar">
			<span v-if="loading" class="loadingInline"><ScaleLoader /> Loading history…</span>
			<span v-else-if="runs.length" class="mutedText">{{runs.length}} most recent run{{runs.length === 1 ? '' : 's'}}, newest first.  Click a row to open it in the operation panel above.</span>
			<button type="button" class="linkButton" :disabled="loading || disabled" @click="$emit('refresh')">refresh</button>
		</div>
		<div v-if="error" class="errorBox">{{error}}</div>
		<div v-else-if="note" class="noteBox">{{note}}</div>
		<div v-else-if="!loading && !runs.length" class="mutedText">No maintenance runs have been recorded yet.</div>
		<div v-if="runs.length" class="tableScroll">
			<table class="historyTable">
				<thead>
					<tr>
						<th>Started</th>
						<th>Operation</th>
						<th>Origin</th>
						<th>Actor</th>
						<th>Status</th>
						<th>Direction</th>
						<th>Databases</th>
						<th title="How long the web apps could not write: the source fence until every web server was running on the target">Writes unavailable</th>
						<th title="Measured database cutover — the data-loss window (max over databases)">DB cutover</th>
						<th title="Measured web-app cutover (database ONLINE → last web server healthy)">Web cutover</th>
						<th>Summary</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="r in runs" :key="r.runId" class="historyRow" :class="{ selectedRow: r.runId === selectedRunId, attentionRow: isAttention(r) && !r.acknowledged }"
						:title="'Run ' + r.runId + ' — click to open'" tabindex="0" @click="$emit('select', r)" @keydown.enter.prevent="$emit('select', r)">
						<td class="nowrap">
							{{formatDateTime(r.startedUtc)}}
							<div class="mutedText small">{{formatElapsed(r.elapsedMs)}}<template v-if="!r.finishedUtc && isActive(r)"> so far</template></div>
						</td>
						<td>
							{{runKindLabel(r.kind)}}
							<span v-if="r.dryRun" class="stateBadge badgeOff" title="Simulated in dry-run mode; nothing touched a live server.">dry-run</span>
						</td>
						<td>{{r.origin || '—'}}</td>
						<td>{{r.actor || '—'}}</td>
						<td>
							<span class="stateBadge" :class="runStatusBadgeClass(r.status)">{{runStatusLabel(r.status)}}</span>
							<div v-if="r.acknowledged" class="mutedText small">acknowledged</div>
						</td>
						<td class="nowrap"><template v-if="r.sourceServer || r.targetServer"><code class="inline">{{r.sourceServer || '?'}}</code> → <code class="inline">{{r.targetServer || '?'}}</code></template><template v-else>—</template></td>
						<td>{{r.dbNames && r.dbNames.length ? r.dbNames.join(', ') : '—'}}</td>
						<td class="nowrap">{{formatMs(r.writesUnavailableMs)}}</td>
						<td class="nowrap">{{formatSeconds(r.dbCutoverSeconds)}}</td>
						<td class="nowrap">{{formatMs(r.webCutoverMs)}}</td>
						<td class="summaryCell">{{r.summary || ''}}</td>
						<td class="nowrap">
							<button v-if="r.hasReport" type="button" class="gradientButton lightBlue smallButton" :disabled="disabled" @click.stop="$emit('report', r)">Report</button>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
	// History of previous maintenance runs (DbMaintenanceHistoryItem[]).  Emits `select(item)` when a row is clicked,
	// `report(item)` for the Report button and `refresh` for the refresh link.
	import { runKindLabel, runStatusLabel, runStatusBadgeClass, formatDateTime, formatElapsed, formatSeconds, formatMs, isActiveRunStatus, isAttentionRunStatus } from './runFormat';

	export default {
		props:
		{
			runs: {
				type: Array,
				default: () => []
			},
			loading: {
				type: Boolean,
				default: false
			},
			/** Grey informational note (e.g. the backend is not available yet). */
			note: {
				type: String,
				default: null
			},
			error: {
				type: String,
				default: null
			},
			/** runId currently shown in the operation panel, highlighted in the table. */
			selectedRunId: {
				type: String,
				default: null
			},
			/** Disables the buttons while an action or dialog is in flight. */
			disabled: {
				type: Boolean,
				default: false
			}
		},
		methods:
		{
			runKindLabel,
			runStatusLabel,
			runStatusBadgeClass,
			formatDateTime,
			formatElapsed,
			formatSeconds,
			formatMs,
			isActive(r)
			{
				return isActiveRunStatus(r.status);
			},
			isAttention(r)
			{
				return isAttentionRunStatus(r.status);
			}
		}
	}
</script>

<style scoped>
	.historyToolbar
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 14px;
		margin-bottom: 6px;
	}

	.loadingInline
	{
		display: inline-flex;
		align-items: center;
		gap: 10px;
	}

	.mutedText
	{
		color: #888888;
	}

	.small
	{
		font-size: 0.85em;
	}

	.linkButton
	{
		background: none;
		border: none;
		padding: 0;
		color: #1A5FB4;
		text-decoration: underline;
		cursor: pointer;
		font: inherit;
		font-size: 0.9em;
	}

		.linkButton:disabled
		{
			color: #999999;
			cursor: default;
		}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		white-space: pre-wrap;
	}

	.noteBox
	{
		background-color: #F4F4F4;
		border: 1px solid #DDDDDD;
		color: #555555;
		padding: 8px 12px;
	}

	.tableScroll
	{
		overflow-x: auto;
	}

	.historyTable
	{
		border-collapse: collapse;
		width: 100%;
		font-size: 0.92em;
	}

		.historyTable th,
		.historyTable td
		{
			text-align: left;
			padding: 5px 8px;
			border-bottom: 1px solid #E4E4E4;
			vertical-align: top;
		}

		.historyTable th
		{
			color: #555555;
			font-size: 0.9em;
			white-space: nowrap;
		}

	.historyRow
	{
		cursor: pointer;
	}

		.historyRow:hover,
		.historyRow:focus
		{
			background-color: #F4F7FB;
			outline: none;
		}

		.historyRow.selectedRow
		{
			background-color: #DDEBFF;
		}

		.historyRow.attentionRow
		{
			background-color: #FFF0F0;
		}

	.nowrap
	{
		white-space: nowrap;
	}

	.summaryCell
	{
		min-width: 200px;
		max-width: 420px;
		word-break: break-word;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}

	.stateBadge
	{
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: bold;
		font-size: 0.9em;
		white-space: nowrap;
	}

	.badgeGood
	{
		background-color: #D4F5D4;
		color: #0A5A0A;
		border: 1px solid #77CC77;
	}

	.badgeWarn
	{
		background-color: #FFEFC9;
		color: #7A5200;
		border: 1px solid #E0B93F;
	}

	.badgeBad
	{
		background-color: #FFE0E0;
		color: #8A0000;
		border: 1px solid #CC6666;
	}

	.badgeInfo
	{
		background-color: #DDEBFF;
		color: #123F7A;
		border: 1px solid #7FAEE8;
	}

	.badgeHold
	{
		background-color: #EADDFF;
		color: #3F1F7A;
		border: 1px solid #B39DE0;
	}

	.badgeOff
	{
		background-color: #EEEEEE;
		color: #666666;
		border: 1px solid #CCCCCC;
	}

	.smallButton
	{
		font-size: 0.9em;
		padding: 3px 10px;
	}

	.gradientButton:disabled
	{
		opacity: 0.55;
		cursor: not-allowed;
	}
</style>
